import React, { useEffect, useState } from "react";
import { Client } from "../../@types/common";
import style from "./ClientsGrid.module.css";
import {
  Button,
  ButtonSize,
  ButtonTheme,
  Loader,
  ThreeDots,
} from "@dbrainio/shared-ui";
import moment from "moment";
import SearchInput from "../SearchInput/SearchInput";
import AddNewButton from "../AddNewButton/AddNewButton";
import classnames from "classnames";
import AddClientForm from "../AddClientForm/AddClientForm";
import Fuse from "fuse.js";
import { AddClientPayload, Api, UpdateClientPayload } from "../../api/api";
import { useDispatch } from "react-redux";
import {
  activateClient,
  deleteClient,
  pauseClient,
  setClient,
  updateClient,
} from "../../store/actions";
import _ from "lodash";
import Badge from "../Badge/Badge";
import { message } from "../../utils/toast";

const options = {
  threshold: 0.1,
  keys: ["license", "client"],
};

export interface ClientsGridProps {
  owner?: string;
  isLoading?: boolean;
  clients: Client[];
  onCopy: (value: string) => void;
}

export default function ClientsGrid({
  clients,
  isLoading: isClientsLoading,
  owner,
  onCopy,
}: ClientsGridProps) {
  const [editedClient, setEditedClient] = useState<Client | null>(null);
  const [currentNavOpenIndex, setCurrentNavIndex] = useState<number | null>(
    null
  );
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isAddForm, setAddForm] = useState(false);
  const [searchResult, setSearchResult] = useState<Client[]>(clients);
  const [search, setSearch] = useState<string>("");
  const [fuse] = useState(new Fuse(clients, options));
  const handleEdit = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    c: Client
  ) => {
    setEditedClient(c);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    fuse.setCollection(clients);

    const searchResult =
      search.length > 0 ? fuse.search(search).map((f) => f.item) : clients;

    setSearchResult(searchResult);

    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const handleDelete = async () => {
    if (!editedClient) return;

    if (window.confirm("Do you really want to delete?")) {
      setIsDeleteLoading(true);
      const response = await Api.deleteClient(editedClient._id);

      if (response.status === 200) {
        if (response.data.success) {
          dispatch(deleteClient({ id: editedClient._id }));
          setEditedClient(null);
          message.success(`${editedClient.client || editedClient.license} has been successfully deleted`);
        }
      } else {
        message.success(`Request failed with status ${response.status}`);
      }

      setIsDeleteLoading(false);
    }
  };

  const handlePause = async (id?: string) => {
    const clientId = editedClient?._id || id;
    if (!clientId) return;

    setIsDeleteLoading(true);
    const response = await Api.pauseClient(clientId);

    if (response.status === 200) {
      if (response.data._id) {
        dispatch(pauseClient({ id: clientId }));
        setEditedClient(null);
        message.success("Token has been successfully paused");
      }
      setIsDeleteLoading(false);
    } else {
      message.error(`Request failed with status ${response.status}`);
    }
  };

  const handleActivate = async (id?: string) => {
    const clientId = editedClient?._id || id;
    if (!clientId) return;

    const response = await Api.activateClient(clientId);

    if (response.status === 200) {
      if (response.data._id) {
        dispatch(activateClient({ id: clientId }));
        setEditedClient(null);
        message.success("Token has been successfully resumed");
      }
      setIsDeleteLoading(false);
    } else {
      message.error(`Request failed with status ${response.status}`);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setSearch(val);
    const searchResult =
      val.length > 0 ? fuse.search(val).map((f) => f.item) : clients;

    setSearchResult(searchResult);
  };

  const handleAdd = () => {
    setAddForm(true);
  };

  const handleClientFormClose = () => {
    setAddForm(false);
    setEditedClient(null);
  };

  const handleAddSubmit = async (payload: AddClientPayload) => {
    setIsLoading(true);

    if (editedClient) {
      const diff = Object.keys(payload).reduce(
        (accum: UpdateClientPayload, next: string) => {
          if (
            !_.isEqual(
              payload[next as keyof AddClientPayload],
              editedClient[next as keyof Client]
            )
          ) {
            return {
              ...accum,
              [next]: payload[next as keyof AddClientPayload],
            };
          }
          return accum;
        },
        {
          id: editedClient._id,
        }
      );

      const response = await Api.updateClient(editedClient._id, {
        ...diff,
      });

      if (response.status === 200) {
        dispatch(updateClient(diff));
        setEditedClient(null);
        message.success("Token has been successfully edited");
      } else {
        message.error(`Error with status code: ${response.status}`);
      }
    } else {
      const response = await Api.addClient(payload);

      if (response.status === 200) {
        dispatch(setClient(response.data));
        setAddForm(false);
        message.success("Token has been successfully created");
      } else {
        message.error(`Error with status code: ${response.status}`);
      }
    }
    setIsLoading(false);
  };

  console.log(clients);

  const mapClient = (c: Client, index: number) => {
    if (!c.counters?.api?.success && !c.limits?.success) {
      return;
    }

    const date = new Date();
    const currentYear = date.getUTCFullYear();
    const currentMonth = `${currentYear}${(date.getUTCMonth() + 1)
      .toString()
      .padStart(2, "0")}`;
    const currentDay = `${currentMonth}${date
      .getUTCDate()
      .toString()
      .padStart(2, "0")}`;

    const {
      alltime: allTimeUsed,
      year: yearUsed,
      month: monthUsed,
      day: dayUsed,
    } = c.counters?.api?.success || { alltime: 0, year: { [currentYear]: 0 }, day: { [currentDay]: 0 }, month: { [currentMonth]: 0 } };
    const {
      alltime: allTimeLimit,
      year: yearLimit,
      month: monthLimit,
      day: dayLimit,
    } = c.limits?.success || { alltime: undefined, year: undefined, day: undefined, month: undefined };

    return (
      <div
        className={classnames(style.row, {
          [style.rowStopped]: c.stopped,
        })}
        key={c._id}
      >
        <div className={style.clientColumn}>
          <div className={style.client}>{c.client || c.license}</div>
          <div className={style.copyToken}>
            <Button
              theme={ButtonTheme.Blue}
              size={ButtonSize.XS}
              onClick={() => onCopy(c.license)}
            >
              Copy token
            </Button>
          </div>
          <div className={style.badges}>
            {c.labels?.includes("hitl") ? <Badge>HITL</Badge> : null}
            {c.labels?.includes("classify") &&
              !c.labels?.includes("recognize") ? (
              <Badge>C</Badge>
            ) : null}
          </div>
        </div>
        <div className={style.limit_box}>
          {allTimeLimit && (
            <div className={style.limit}>
              <span
                className={classnames(
                  style.spent,
                  allTimeUsed <= allTimeLimit ? style.green : style.orange
                )}
              >
                {allTimeUsed}
              </span>
              /<span className={style.max}>{allTimeLimit}</span>
              <div className={style.label}>All time</div>
            </div>
          )}
          {allTimeLimit === null && (
            <div className={style.limit}>
              <span className={style.green}>{allTimeUsed}/Unlimited</span>
              <div className={style.label}>All time</div>
            </div>
          )}
          {yearLimit && (
            <div className={style.limit}>
              <span
                className={classnames(
                  style.spent,
                  yearUsed[currentYear] <= yearLimit
                    ? style.green
                    : style.orange
                )}
              >
                {yearUsed[currentYear] || 0}
              </span>
              /<span className={style.max}>{yearLimit}</span>
              <div className={style.label}>Annually</div>
            </div>
          )}
          {monthLimit && (
            <div className={style.limit}>
              <span
                className={classnames(
                  style.spent,
                  monthUsed?.[currentMonth] <= monthLimit
                    ? style.green
                    : style.orange
                )}
              >
                {monthUsed?.[currentMonth]}
              </span>
              /<span className={style.max}>{monthLimit}</span>
              <div className={style.label}>Monthly</div>
            </div>
          )}
          {dayLimit && (
            <div className={style.limit}>
              <span
                className={classnames(
                  style.spent,
                  dayUsed?.[currentDay] <= dayLimit ? style.green : style.orange
                )}
              >
                {dayUsed?.[currentDay]}
              </span>
              /<span className={style.max}>{dayLimit}</span>
              <div className={style.label}>Daily</div>
            </div>
          )}
        </div>
        <div className={style.expirationDate}>
          {c.expire ? moment.unix(c.expire).format("MMM DD, YYYY") : "–"}
        </div>
        <div className={style.owner}>{c.owner}</div>
        <div className={style.isStopped}>
          {c.stopped ? <Badge color="#E08B09">Paused</Badge> : null}
        </div>
        <div className={style.extra}>
          {
            <ThreeDots
              onClick={() =>
                currentNavOpenIndex === index
                  ? setCurrentNavIndex(null)
                  : setCurrentNavIndex(index)
              }
              onMouseLeave={() => setCurrentNavIndex(null)}
              isRevealed={index === currentNavOpenIndex}
              popupContent={
                <div className={style.threeDotsPopup}>
                  {c.website && (
                    <a
                      href={c.website}
                      rel="noopener noreferrer"
                      target="_blank"
                      className={style.link}
                    >
                      Website
                    </a>
                  )}
                  {c.pipeline_link && (
                    <a
                      href={c.pipeline_link}
                      rel="noopener noreferrer"
                      target="_blank"
                      className={style.link}
                    >
                      Pipeline
                    </a>
                  )}

                  {c.stopped ? (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleActivate(c._id);
                      }}
                      className={classnames(style.link, style.green)}
                    >
                      Resume
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handlePause(c._id);
                      }}
                      className={classnames(style.link, style.orange)}
                    >
                      Pause
                    </button>
                  )}
                  <button
                    onClick={(e) => handleEdit(e, c)}
                    className={style.link}
                  >
                    Edit
                  </button>
                </div>
              }
            />
          }
        </div>
      </div>
    );
  };

  // function rowRenderer({
  //   key, // Unique key within array of rows
  //   index, // Index of row within collection
  //   isScrolling, // The List is currently being scrolled
  //   isVisible, // This row is visible within the List (eg it is not an overscanned row)
  //   style, // Style object to be applied to row (to position it)
  // }: ListRowProps) {
  //   return (
  //     <div key={key} style={style}>
  //       {mapClient(searchResult[index], index)}
  //     </div>
  //   )
  // }

  return (
    <div className={style.root}>
      <div className={style.search}>
        <SearchInput onChange={handleSearch} value={search} />
      </div>

      <div className={style.grid}>
        <div className={style.header}>
          <div className={style.column}>Client</div>
          <div className={style.column}>Total/Limit</div>
          <div className={style.column}>Expiration Date</div>
          <div className={style.column}>Owner</div>
        </div>
        {isAddForm || editedClient ? (
          <AddClientForm
            onClose={handleClientFormClose}
            owner={owner}
            onCopy={onCopy}
            onSubmit={handleAddSubmit}
            isLoading={isLoading}
            isDeleteLoading={isDeleteLoading}
            editedClient={editedClient}
            onDelete={handleDelete}
            onPause={handlePause}
            onActivate={handleActivate}
          />
        ) : (
          <div className={style.addButton}>
            <AddNewButton onClick={handleAdd} />{" "}
          </div>
        )}
        <div className={style.clients}>
          {isClientsLoading ? (
            <Loader color="#000" size={8} fullscreen />
          ) : (
            searchResult.map(mapClient)
          )}
        </div>
      </div>
    </div>
  );
}
